'use client';
import React from 'react';
import { AlertTriangle, CheckCircle, Zap } from 'lucide-react';
import { Distributor, QualStage, getRankDotColor } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';

interface Props {
  distributor: Distributor;
  stage: QualStage;
  gsvReq: number;
}

export default function QualificationCard({ distributor, stage, gsvReq }: Props) {
  const gsvGap = gsvReq > 0 ? Math.max(0, gsvReq - distributor.gsv) : 0;
  const psvOk = distributor.psv >= 100;
  const gsvPct = gsvReq > 0 ? Math.min((distributor.gsv / gsvReq) * 100, 100) : 100;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2.5 hover:border-zinc-700 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getRankDotColor(distributor.rank)}`} />
        <span className="text-xs font-semibold text-zinc-200 truncate flex-1">{distributor.name}</span>
        {distributor.pexecMonthsRemaining !== null && (
          <AlertTriangle size={11} className="text-orange-400 flex-shrink-0" />
        )}
        {distributor.qualStage !== 'None' && distributor.qualStage !== 'Qualified' && (
          <Zap size={11} className="text-amber-400 flex-shrink-0" />
        )}
      </div>

      <RankBadge rank={distributor.rank} size="xs" />

      <div className="mt-2 space-y-1.5">
        {/* PSV */}
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-zinc-600 uppercase tracking-wider">PSV</span>
          <div className="flex items-center gap-1">
            {psvOk ? <CheckCircle size={9} className="text-emerald-400" /> : <AlertTriangle size={9} className="text-red-400" />}
            <span className={`text-[10px] font-mono font-semibold ${psvOk ? 'text-zinc-300' : 'text-red-400'}`}>{distributor.psv}</span>
          </div>
        </div>

        {/* GSV */}
        {gsvReq > 0 && (
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[9px] text-zinc-600 uppercase tracking-wider">GSV</span>
              <span className="text-[10px] font-mono font-semibold text-zinc-300">{(distributor.gsv / 1000).toFixed(1)}K</span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-1">
              <div
                className={`h-1 rounded-full transition-all ${gsvGap === 0 ? 'bg-emerald-500' : 'bg-violet-500'}`}
                style={{ width: `${gsvPct}%` }}
              />
            </div>
            {gsvGap > 0 && (
              <p className="text-[9px] text-red-400 mt-0.5">−{(gsvGap / 1000).toFixed(1)}K to qualify</p>
            )}
          </div>
        )}

        {/* ADR */}
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-zinc-600 uppercase tracking-wider">ADR</span>
          <span className={`text-[9px] font-semibold ${distributor.adrActive ? 'text-emerald-400' : 'text-red-400'}`}>
            {distributor.adrActive ? '✓ Active' : '✗ Off'}
          </span>
        </div>

        {/* PEXEC countdown */}
        {distributor.pexecMonthsRemaining !== null && (
          <div className="mt-1 bg-orange-950/30 border border-orange-500/20 rounded-lg px-2 py-1">
            <p className="text-[9px] text-orange-400 font-semibold">PEXEC: {distributor.pexecMonthsRemaining}mo remaining</p>
          </div>
        )}

        {/* Grace month */}
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Grace</span>
          <span className={`text-[9px] font-semibold ${distributor.graceMonthUsed ? 'text-orange-400' : 'text-emerald-400'}`}>
            {distributor.graceMonthUsed ? 'Used' : 'Available'}
          </span>
        </div>
      </div>
    </div>
  );
}